import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { solicitacoesTitularService } from '@/services/solicitacoesTitularService'
import { privacidadeService } from '@/services/privacidadeService'
import {
  CANAL_SOLICITACAO_LABEL,
  ESTADOS_COM_FUNDAMENTO,
  ESTADO_SOLICITACAO_LABEL,
  EstadoSolicitacaoTitular,
  EventoSolicitacaoTitular,
  Expurgo,
  SISTEMAS_CHECKLIST,
  SolicitacaoTitular,
  TIPO_EVENTO_SOLICITACAO_LABEL,
  TIPO_SOLICITACAO_LABEL,
} from '@/types/privacidade'
import { formatDate, formatDateTime } from '@/utils/formatters'
import { ConfirmacaoPorCodigoDialog } from './ConfirmacaoPorCodigoDialog'
import { ChipStatusExpurgo, mensagemErroPrivacidade, rotuloAtor, useAcessoPrivacidade } from './comum'

/** Só para frente (AN-12 §4.2) — quem diz para onde pode ir é o servidor (`proximosEstados`). */
export const proximosEstados = (s: SolicitacaoTitular): EstadoSolicitacaoTitular[] => s.proximosEstados ?? []

/** Expurgo vinculado que ainda impede "Atendida": nem concluído, nem encerrado sem efeito. */
const expurgoEmAberto = (e: Expurgo) => !['CONCLUIDO', 'REJEITADO', 'CANCELADO'].includes(e.status)

interface Props {
  protocolo: string | null
  onFechar: () => void
  onAlterado: () => void
}

/**
 * Detalhe da solicitação do titular (AN-12 §4, §12): linha do tempo, checklist de sistemas
 * verificados, expurgos vinculados (pela fila filtrada pelo protocolo) e as ações do DPO.
 * A anonimização de fato é do ADMIN, e nunca de quem a encaminhou (dupla checagem).
 */
export const SolicitacaoTitularDrawer: FC<Props> = ({ protocolo, onFechar, onAlterado }) => {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const acesso = useAcessoPrivacidade()
  const [s, setS] = useState<SolicitacaoTitular | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [eventos, setEventos] = useState<EventoSolicitacaoTitular[] | null>(null)
  const [erroEventos, setErroEventos] = useState<string | null>(null)
  const [expurgos, setExpurgos] = useState<Expurgo[] | null>(null)
  const [erroExpurgos, setErroExpurgos] = useState(false)

  const [para, setPara] = useState<EstadoSolicitacaoTitular | ''>('')
  const [nota, setNota] = useState('')
  const [prazo, setPrazo] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [confirmarExecucao, setConfirmarExecucao] = useState(false)

  const carregar = useCallback(async (p: string) => {
    setErro(null)
    setErroEventos(null)
    setErroExpurgos(false)
    try {
      const atual = await solicitacoesTitularService.buscar(p)
      setS(atual)
      setPrazo(atual.prazoRespostaEm ? atual.prazoRespostaEm.slice(0, 10) : '')
    } catch (e) {
      setErro(mensagemErroPrivacidade(e, 'Falha ao carregar a solicitação'))
      return
    }
    solicitacoesTitularService
      .eventos(p)
      .then(setEventos)
      .catch((e) => setErroEventos(mensagemErroPrivacidade(e, 'Falha ao carregar a linha do tempo')))
    privacidadeService
      .expurgos({ solicitacao: p })
      .then(setExpurgos)
      .catch(() => setErroExpurgos(true))
  }, [])

  useEffect(() => {
    setS(null)
    setEventos(null)
    setExpurgos(null)
    setPara('')
    setNota('')
    if (protocolo) carregar(protocolo)
  }, [protocolo, carregar])

  const aposAlterar = async (msg: string, atualizada: SolicitacaoTitular | null) => {
    enqueueSnackbar(msg, { variant: 'success' })
    if (atualizada && typeof atualizada === 'object' && 'protocolo' in atualizada) setS(atualizada)
    if (protocolo) await carregar(protocolo)
    onAlterado()
  }

  const acao = async (fn: () => Promise<SolicitacaoTitular>, ok: string, falha: string) => {
    setSalvando(true)
    try {
      await aposAlterar(ok, await fn())
      return true
    } catch (e) {
      enqueueSnackbar(mensagemErroPrivacidade(e, falha), { variant: 'error' })
      return false
    } finally {
      setSalvando(false)
    }
  }

  if (!protocolo) return <Drawer anchor="right" open={false} onClose={onFechar} />

  const expurgosAbertos = (expurgos ?? []).filter(expurgoEmAberto)
  const precisaFundamento = !!para && ESTADOS_COM_FUNDAMENTO.includes(para)
  const atendidaBloqueada = para === 'ATENDIDA' && expurgosAbertos.length > 0
  const verificados = new Map((s?.sistemasVerificados ?? []).map((v) => [v.sistema, v]))
  const execucaoPendente = !!s?.execucaoAnonimizacaoPendente
  const encaminheiEu = !!s?.aguardandoExecucaoPor && s.aguardandoExecucaoPor === acesso.usuarioId
  const aberta = !!s && proximosEstados(s).length > 0

  return (
    <Drawer anchor="right" open onClose={onFechar}>
      <Box sx={{ width: { xs: '100vw', sm: 560 }, p: 3 }} role="dialog" aria-label={protocolo}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {protocolo}
          </Typography>
          <IconButton onClick={onFechar} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
        {erro && <Alert severity="error">{erro}</Alert>}
        {!s && !erro && <CircularProgress size={24} aria-label="Carregando solicitação" />}

        {s && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" color="primary" variant="outlined" label={ESTADO_SOLICITACAO_LABEL[s.estado]} />
              <Chip size="small" variant="outlined" label={TIPO_SOLICITACAO_LABEL[s.tipo] ?? s.tipo} />
              <Chip size="small" variant="outlined" label={`Canal: ${CANAL_SOLICITACAO_LABEL[s.canal] ?? s.canal}`} />
            </Stack>

            <Box>
              <Typography variant="body2">
                <strong>Titular:</strong> {s.titularNome || '—'}
                {s.titularDocumentoFinal ? ` · CPF final ${s.titularDocumentoFinal}` : ''}
              </Typography>
              {!s.identificadoPorDocumento && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Sem identificação por CPF.
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Contato:</strong> {s.contatoResposta || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Recebida em:</strong> {formatDateTime(s.recebidaEm)}
              </Typography>
              {s.prazoRespostaEm && (
                <Typography variant="body2" data-testid="prazo-informativo">
                  <strong>Prazo informativo de resposta:</strong> {formatDate(s.prazoRespostaEm)}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Responsável:</strong>{' '}
                {s.responsavelId ? rotuloAtor(s.responsavelId, acesso.usuarioId) : 'ninguém'}
                {acesso.dpo && acesso.usuarioId && s.responsavelId !== acesso.usuarioId && aberta && (
                  <Button
                    size="small"
                    sx={{ ml: 1 }}
                    disabled={salvando}
                    onClick={() =>
                      acao(
                        () => solicitacoesTitularService.atribuirResponsavel(s.protocolo, acesso.usuarioId as string),
                        'Você é o responsável',
                        'Falha ao assumir'
                      )
                    }
                  >
                    Assumir
                  </Button>
                )}
              </Typography>
              {acesso.dpo && aberta && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <TextField
                    type="date"
                    size="small"
                    label="Prazo informativo"
                    value={prazo}
                    onChange={(e) => setPrazo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'aria-label': 'Prazo informativo de resposta' }}
                  />
                  <Button
                    size="small"
                    disabled={salvando || prazo === (s.prazoRespostaEm ?? '').slice(0, 10)}
                    onClick={() =>
                      acao(
                        () => solicitacoesTitularService.definirPrazo(s.protocolo, prazo || null),
                        prazo ? 'Prazo informativo registrado' : 'Prazo informativo removido',
                        'Falha ao registrar o prazo'
                      )
                    }
                  >
                    {prazo ? 'Salvar prazo' : 'Remover prazo'}
                  </Button>
                </Stack>
              )}
              {s.descricao && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                  {s.descricao}
                </Typography>
              )}
              {s.fundamentoDecisao && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Fundamento da decisão: {s.fundamentoDecisao}
                </Alert>
              )}
            </Box>

            {execucaoPendente && acesso.admin && (
              encaminheiEu ? (
                <Alert severity="info" data-testid="aviso-execucao">
                  Você encaminhou esta solicitação para execução: a anonimização precisa ser feita por outra pessoa
                  (ADMIN).
                </Alert>
              ) : (
                <Alert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" onClick={() => setConfirmarExecucao(true)}>
                      Executar anonimização
                    </Button>
                  }
                >
                  Anonimização aprovada pelo DPO, aguardando execução.
                </Alert>
              )
            )}

            <Divider />
            <Typography variant="subtitle2">Expurgos vinculados</Typography>
            {erroExpurgos ? (
              <Typography variant="caption" color="text.secondary">
                Não foi possível consultar a fila de expurgo.
              </Typography>
            ) : !expurgos ? (
              <CircularProgress size={18} aria-label="Carregando expurgos" />
            ) : expurgos.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum expurgo vinculado a este protocolo.
              </Typography>
            ) : (
              <Stack spacing={0.5} data-testid="expurgos-vinculados">
                {expurgos.map((e) => (
                  <Stack key={e.id} direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={600}>
                      {e.codigo}
                    </Typography>
                    <ChipStatusExpurgo status={e.status} />
                  </Stack>
                ))}
              </Stack>
            )}
            {acesso.permitido && (
              <Box>
                <Button
                  size="small"
                  onClick={() => navigate(`/privacidade-lgpd/expurgos?solicitacao=${encodeURIComponent(s.protocolo)}`)}
                >
                  Abrir na fila de expurgo
                </Button>
              </Box>
            )}

            <Divider />
            <Typography variant="subtitle2">Sistemas verificados</Typography>
            <Stack spacing={0.5} data-testid="checklist-sistemas">
              {SISTEMAS_CHECKLIST.map((item) => {
                const registro = verificados.get(item.chave)
                const v = registro?.verificado ? registro : undefined
                return (
                  <Stack key={item.chave} direction="row" spacing={1} alignItems="center">
                    {v ? (
                      <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                      <Box sx={{ width: 20 }} />
                    )}
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {item.rotulo}
                      {v && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' '}
                          — {rotuloAtor(v.por, acesso.usuarioId)}
                          {v.em ? ` · ${formatDateTime(v.em)}` : ''}
                          {v.observacao ? ` · ${v.observacao}` : ''}
                        </Typography>
                      )}
                    </Typography>
                    {acesso.dpo && aberta && (
                      <Button
                        size="small"
                        disabled={salvando}
                        aria-label={`${v ? 'Desmarcar' : 'Marcar'} ${item.rotulo} como verificado`}
                        onClick={() =>
                          acao(
                            () => solicitacoesTitularService.marcarSistema(s.protocolo, item.chave, !v),
                            v ? 'Verificação desmarcada' : 'Sistema marcado como verificado',
                            'Falha ao marcar'
                          )
                        }
                      >
                        {v ? 'Desmarcar' : 'Marcar verificado'}
                      </Button>
                    )}
                  </Stack>
                )
              })}
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Linha do tempo</Typography>
            {erroEventos && <Alert severity="error">{erroEventos}</Alert>}
            {!eventos && !erroEventos && <CircularProgress size={18} aria-label="Carregando linha do tempo" />}
            {eventos && (
              <Stack spacing={1} data-testid="linha-do-tempo">
                {eventos.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Sem eventos.
                  </Typography>
                )}
                {eventos.map((ev) => (
                  <Box key={ev.id} sx={{ borderLeft: 3, borderColor: 'divider', pl: 1.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {ev.para
                        ? `${ev.de ? `${ESTADO_SOLICITACAO_LABEL[ev.de]} → ` : ''}${ESTADO_SOLICITACAO_LABEL[ev.para]}`
                        : TIPO_EVENTO_SOLICITACAO_LABEL[ev.tipo as keyof typeof TIPO_EVENTO_SOLICITACAO_LABEL] ?? ev.tipo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(ev.em)} · {rotuloAtor(ev.autor, acesso.usuarioId)}
                    </Typography>
                    {ev.nota && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {ev.nota}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}

            <Divider />
            {proximosEstados(s).length === 0 ? (
              <Alert severity="info">Solicitação encerrada — estado terminal.</Alert>
            ) : !acesso.dpo ? (
              <Alert severity="info">Somente o DPO muda o estado da solicitação.</Alert>
            ) : (
              <Stack spacing={2}>
                <TextField
                  select
                  size="small"
                  label="Mudar para"
                  value={para}
                  onChange={(e) => setPara(e.target.value as EstadoSolicitacaoTitular)}
                >
                  {proximosEstados(s).map((e) => (
                    <MenuItem key={e} value={e}>
                      {ESTADO_SOLICITACAO_LABEL[e]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={precisaFundamento ? 'Fundamento da decisão' : 'Nota'}
                  required={precisaFundamento}
                  multiline
                  rows={3}
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  helperText={
                    precisaFundamento
                      ? 'Obrigatório nesta transição. Fica na linha do tempo, sem edição.'
                      : 'Fica na linha do tempo, sem edição.'
                  }
                />
                {atendidaBloqueada && (
                  <Alert severity="warning">
                    Há {expurgosAbertos.length} expurgo(s) vinculado(s) ainda não concluído(s). A solicitação só pode
                    ser dada como atendida depois que eles concluírem.
                  </Alert>
                )}
                <Button
                  variant="contained"
                  disabled={!para || salvando || atendidaBloqueada || (precisaFundamento && !nota.trim())}
                  onClick={async () => {
                    if (!para) return
                    const ok = await acao(
                      () =>
                        solicitacoesTitularService.transicionar(s.protocolo, {
                          para,
                          nota: nota.trim() || undefined,
                        }),
                      `${s.protocolo} → ${ESTADO_SOLICITACAO_LABEL[para]}`,
                      'Falha ao mudar o estado'
                    )
                    if (ok) {
                      setPara('')
                      setNota('')
                    }
                  }}
                >
                  {salvando ? 'Registrando…' : 'Registrar transição'}
                </Button>
              </Stack>
            )}
          </Stack>
        )}

        {s && (
          <ConfirmacaoPorCodigoDialog
            aberto={confirmarExecucao}
            codigo={s.protocolo}
            titulo={`Executar anonimização — ${s.protocolo}`}
            descricao="Substitui nome e e-mail do titular por valores anonimizados, desabilita o acesso e republica a projeção do usuário. Não pode ser desfeito."
            rotuloConfirmar="Executar anonimização"
            corConfirmar="error"
            processando={salvando}
            onCancelar={() => setConfirmarExecucao(false)}
            onConfirmar={async () => {
              await acao(
                () => solicitacoesTitularService.executarAnonimizacao(s.protocolo),
                'Anonimização executada',
                'Falha ao executar a anonimização'
              )
              setConfirmarExecucao(false)
            }}
          />
        )}
      </Box>
    </Drawer>
  )
}

export default SolicitacaoTitularDrawer
